import { Component } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"

/** Geography the model is processed against. Updated in place when a public viewer switches extent. */
export interface ProjectGeography {
  extent: number[]
  zoom: number
  mask: boolean
  maskLayer: string
  maskCQL: string
  maskShape?: string
  externalLink?: string
}

/**
 * Components keep the project geography they were constructed with, including
 * copies of the extent and zoom. Public extent switching reuses the same
 * editor, so those copies and any grids built for the previous area have to
 * be refreshed before the next run.
 */
/** Identifies the area a grid was built for, so a later area cannot reuse it. */
export function projectGeographyKey(props: {
  extent?: number[]
  zoom?: number
  mask?: boolean
  maskLayer?: string
  maskCQL?: string
  maskShape?: string
  externalLink?: string
}): string {
  const extent = (props.extent ?? []).map(coord => Math.round(coord))
  return [
    props.zoom ?? "",
    extent.join(","),
    props.mask ? 1 : 0,
    props.maskLayer ?? "",
    props.maskCQL ?? "",
    props.maskShape ?? "",
    props.externalLink ?? "",
  ].join("|")
}

const geographyStamps = new WeakMap<object, string>()

export function stampGridGeography(grid: object, key: string): void {
  geographyStamps.set(grid, key)
}

export function gridGeographyKey(grid: object): string | undefined {
  return geographyStamps.get(grid)
}

export function applyProjectProperties(component: BaseComponent, props: ProjectGeography): void {
  const self = component as BaseComponent & Record<string, unknown>
  const current = self.projectProps
  if (current && typeof current === "object") {
    Object.assign(current, {
      extent: props.extent,
      zoom: props.zoom,
      mask: props.mask,
      maskLayer: props.maskLayer,
      maskCQL: props.maskCQL,
      maskShape: props.maskShape,
      externalLink: props.externalLink,
    })
  }
  if ("projectExtent" in self) self.projectExtent = props.extent
  if ("projectZoom" in self) self.projectZoom = props.zoom
  if ("maskMode" in self) self.maskMode = props.mask
  if ("maskLayer" in self) self.maskLayer = props.maskLayer
  if ("maskCQL" in self) self.maskCQL = props.maskCQL
  if ("maskShape" in self) self.maskShape = props.maskShape
  resetCachedField(self, "cachedData", undefined)
  resetCachedField(self, "categoricalData", null)
  resetCachedField(self, "boolData", null)
  resetCachedField(self, "tileGridsCache", undefined)
  resetCachedField(self, "outputCache", undefined)
  if (Array.isArray(self.previousInput)) self.previousInput = []
}

function resetCachedField(self: Record<string, unknown>, key: string, empty: null | undefined) {
  if (!(key in self)) return
  const value = self[key]
  if (value instanceof Map) {
    value.clear()
    return
  }
  self[key] = empty
}

export abstract class BaseComponent extends Component {
  category?: string
  contextMenuName?: string
  /**
   * Plain-text summary shown in the Toolbox sidebar when the user clicks the
   * component card. Optional — cards without a description show a generic
   * placeholder instead.
   */
  description?: string

  // Original worker method override
  abstract worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]): Promise<void> | void;
}
